import React from 'react'

// Constants for viewport breakpoints
export const MAX_VIEWPORT = 1200
export const MIN_VIEWPORT = 600

// Common interface for responsive props
export interface ResponsiveProps {
  maxSize: number
  minSize: number
}

// Common interface for base typography props
export interface BaseTypographyProps {
  children: React.ReactNode
  className?: string
  color?: string
  style?: React.CSSProperties
  id?: string
}

// Function to apply responsive font sizing
export const applyResponsiveFontSize = (
  styles: React.CSSProperties,
  responsive?: ResponsiveProps,
): React.CSSProperties => {
  if (!responsive) return styles

  const combinedStyles = { ...styles }

  // Set CSS variables for responsive font sizing
  combinedStyles['--min-font-size'] = `${responsive.minSize}px`
  combinedStyles['--max-font-size'] = `${responsive.maxSize}px`
  combinedStyles['--min-viewport'] = `${MIN_VIEWPORT}px`
  combinedStyles['--max-viewport'] = `${MAX_VIEWPORT}px`

  // Use a precise formula that respects viewport boundaries
  combinedStyles.fontSize = `clamp(
    ${responsive.minSize}px,
    ${responsive.minSize}px + (${responsive.maxSize} - ${responsive.minSize}) *
    ((100vw - ${MIN_VIEWPORT}px) / (${MAX_VIEWPORT} - ${MIN_VIEWPORT})),
    ${responsive.maxSize}px
  )`

  return combinedStyles
}

// Function to apply color styling
export const applyColorStyling = (styles: React.CSSProperties, color?: string): React.CSSProperties => {
  if (!color) return styles

  return {
    ...styles,
    color,
  }
}

// Combine all style modifications
export const createCombinedStyles = (
  baseStyles: React.CSSProperties = {},
  color?: string,
  responsive?: ResponsiveProps,
): React.CSSProperties => {
  let combinedStyles = { ...baseStyles }

  // Apply color if provided
  combinedStyles = applyColorStyling(combinedStyles, color)

  // Apply responsive font sizing if provided
  combinedStyles = applyResponsiveFontSize(combinedStyles, responsive)

  return combinedStyles
}
